import React from 'react';
import MicNoneOutlinedIcon from '@material-ui/icons/MicNoneOutlined';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';

import { SUPPORTED_LANGUAGES_COUNT } from 'src/data/generation/const';
import { CheckmarkIcon } from 'src/assets/icons/Checkmark';
import {
  SUBSCRIPTION_PLAN,
  DEFAULT_SUBSCRIPTION_PLAN,
  SUBSCRIPTION_BASIC_TIER,
  DEFAULT_BASIC_TIER,
  DETAILS_TABLE_ICONS,
  SUBSCRIPTION_DATA_DRIVEN_TIER,
  DEFAULT_DD_TIER,
  SUBSCRIPTION_PLAN_DEPRECATED,
  SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
} from './const';
import { APIAccessIcon } from 'src/assets/icons/subscription/APIAccess';
import { CIPIcon } from 'src/assets/icons/subscription/CIP';
import { WebsiteConversionOptimizationIcon } from 'src/assets/icons/subscription/WebsiteConversionOptimization';
import { DedicatedCSMIcon } from 'src/assets/icons/subscription/DedicatedCSM';
import { EnterpriseGradeSecurityIcon } from 'src/assets/icons/subscription/EnterpriseGradeSecurity';
import { AdminRoleManagementIcon } from 'src/assets/icons/subscription/AdminRoleManagement';
import { BrandManagementCenterIcon } from 'src/assets/icons/subscription/BrandManagementCenter';
import { CustomScoringIcon } from 'src/assets/icons/subscription/CustomScoring';

export const clampSubscriptionPlan = (plan, defaultValue = DEFAULT_SUBSCRIPTION_PLAN) => {
  const values = Object.values(SUBSCRIPTION_PLAN);
  if (values.includes(plan)) {
    return plan;
  }
  return defaultValue;
};

export const isSubscriptionPlanDeprecated = (plan) =>
  Object.values(SUBSCRIPTION_PLAN_DEPRECATED).includes(plan);

export const isStarterPlan = (plan) => plan === SUBSCRIPTION_PLAN.starter;
export const isBasicPlan = (plan) => plan === SUBSCRIPTION_PLAN.basic;
export const isDDTeamsPlan = (plan) => plan === SUBSCRIPTION_PLAN.dd_teams;
export const isEnterpriseOrAcePlan = (plan) =>
  [SUBSCRIPTION_PLAN.enterprise, SUBSCRIPTION_PLAN.business].includes(plan);
export const isSelfServicePlan = (plan) => {
  return ![SUBSCRIPTION_PLAN.enterprise, SUBSCRIPTION_PLAN.business].includes(plan);
};
export const isDataDrivenPlan = (plan, tier) => isDDTeamsPlan(plan);

export const isStarterOrBasicPlan = (plan) => isStarterPlan(plan) || isBasicPlan(plan);

export const isBusinessPlan = (plan) => plan === SUBSCRIPTION_PLAN.business;

export const isPlanWithTiers = (plan) =>
  plan === SUBSCRIPTION_PLAN.dataDriven || plan === SUBSCRIPTION_PLAN.proAgency;

const FEATURE_DD_COPYWRITING_TOOLS = {
  text: '200+ Data-driven copywriting tools',
  tooltip:
    "Drive performance across your ads, social posts, landing pages, emails and more.\n\nAnyword's massive proprietary data sets let you create highly effective copy for any marketing channel.",
};

const FEATURE_REAL_TIME_PREDICTIVE_PERFORMANCE_SCORE = {
  text: 'Real-time predictive performance score and analytics',
  tooltip: 'Get real-time scoring and analytics for every copy edit you make.',
};

const FEATURE_REAL_TIME_SCORING_ANALYTICS = {
  text: 'Improve and boost scores for your copy in real time!',
  tooltip: 'Have Anyword rewrite any existing copy to improve its predictive performance score.',
};

const FEATURE_LANGUAGES = {
  text: `${SUPPORTED_LANGUAGES_COUNT} languages`,
};

const FEATURE_AI_TOOLS = { text: '100+ AI writing tools' };

const FEATURE_API_ACCESS = {
  text: 'API access',
};

const PLAN_PROPS = {
  [SUBSCRIPTION_PLAN.basic]: {
    label: 'Free',
    description:
      'Everything you need to write professional copy 10x faster. No time limit, no credit card required.',
    creditsPerMonth: '1,000',
    seats: '1',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>1,000</b> word credits per month',
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      {
        text: 'Blog post wizard',
      },
      {
        text: 'US English supported',
      },
      {
        text: '1 seat',
        seatsLimitFeature: true,
      },
    ],
    featureGroups: [
      {
        title: 'Data-Driven Copy Generation',
        icon: <CreateOutlinedIcon />,
        features: [
          '100+ performance-driven templates',
          'Blog Wizard',
          'Unlimited one-click boost performance',
          'Predictive performance scores & analytics',
          'High-performing talking point suggestions',
        ],
      },
      {
        title: 'Brand voice',
        icon: <MicNoneOutlinedIcon />,
        features: [
          '1 tone of voice',
          '2 target audiences',
          '3 brand rules',
          '1 messaging bank asset',
          '1 custom copywriting formula',
        ],
      },
    ],
  },
  [SUBSCRIPTION_PLAN.basicTiered]: {
    label: 'Basic',
    description: 'Everything you need to write professional copy 10x faster.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    creditsPerMonth: '20,000 — 300,000',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>{wordsLimit}</b> word credits per month',
        wordCreditsFeature: true,
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      {
        text: 'Blog post wizard',
      },
      FEATURE_LANGUAGES,
      {
        text: 'Multiple seats',
        seatsLimitFeature: true,
      },
    ],
    tiers: SUBSCRIPTION_BASIC_TIER,
    defaultTier: DEFAULT_BASIC_TIER,
  },
  [SUBSCRIPTION_PLAN.dataDriven]: {
    label: 'Data-Driven',
    description:
      'Everything you need to create on-brand content at scale that converts! With real-time scoring, analytics and optimizations!',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    creditsPerMonth: '30,000 — Unlimited',
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>{wordsLimit}</b> word credits per month',
        wordCreditsFeature: true,
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      FEATURE_REAL_TIME_PREDICTIVE_PERFORMANCE_SCORE,
      FEATURE_REAL_TIME_SCORING_ANALYTICS,
      { text: 'Blog post wizard' },
      FEATURE_LANGUAGES,
      {
        text: 'Multiple seats',
        seatsLimitFeature: true,
      },
    ],
    tiers: SUBSCRIPTION_DATA_DRIVEN_TIER,
    defaultTier: DEFAULT_DD_TIER,
  },

  [SUBSCRIPTION_PLAN.professional]: {
    label: 'Professional',
    description:
      'Boost the performance of your copy across the marketing funnel with custom algorithms — trained on your actual performance data.',
    subtitle:
      'A must-have for growth marketers, paid media teams, content teams, social media teams, and demand generation teams.',
    numberOfMonthsDiscount: 4,
    creditsPerMonth: 'Unlimited',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: 'Ads, Emails',
    support: '<span>Dedicated customer\nsuccess manager</span>',
    importAdsFromSocial: DETAILS_TABLE_ICONS.check,
    copyVariationsInformed: DETAILS_TABLE_ICONS.check,
    apiAccess: DETAILS_TABLE_ICONS.check,
    policyComplianceDetection: 'Facebook, Google',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    features: [
      {
        text: 'Leverage copy algorithms custom built for your audiences and products. Increase conversions and engagement with better copy generations, and highly accurate copy predictions',
      },
      {
        text: 'Workspaces for every brand and pod in your organization. Includes role-based permissioning, data security, and multi-tenancy',
      },
      {
        text: 'Integrate with 3rd party platforms to support your existing workflows',
      },
      { text: 'Create automations to craft any copy tailored to your specifications at scale' },
      {
        text: "Leverage data sources such as customers' reviews to inform your custom copy algorithm",
      },

      { text: 'Discounted word credit prices to match your scale needs' },
    ],
  },

  [SUBSCRIPTION_PLAN.website]: {
    label: 'Website',
    description:
      'Connect Anyword to your website and serve every visitor an optimized message based on their intent.',
    subtitle: 'Ideal for digital customer experience, web, and demand generation teams.',
    numberOfMonthsDiscount: 4,
    creditsPerMonth: 'Unlimited',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Improve your existing\nlanding pages continuously\nor write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: '<span>Dedicated customer\nsuccess manager</span>',
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    features: [
      { text: 'Generate copy at scale that outperforms your existing website copy' },
      { text: 'Leverage landing page and website-specific data-driven copy tools' },
      { text: 'Optimize copy across your website in real-time based on referer source and UTM' },
      { text: 'Connect target audiences and cohorts to sets of personalized web copy' },
      {
        text: 'Connect your website copy to your ad copy, aligning them and continously optimizing both',
      },
      { text: 'Integrate and get started in seconds' },
      { text: 'Test many copy variations simultaneously, both your own and AI created' },
    ],
  },

  [SUBSCRIPTION_PLAN.business]: {
    label: 'Business',
    description:
      'Performance-driven Gen AI & Copy Intelligence custom-built for your brand and organization. For 5+ users.',
    featureGroups: [
      {
        title: 'Copy Intelligence Platform with Unlimited Channels & Campaigns',
        icon: <CIPIcon />,
      },
      {
        title: 'Custom Scoring AI Models trained on your performance Data',
        icon: <CustomScoringIcon />,
      },
      {
        title: 'Full Access Website Conversion Optimization',
        icon: <WebsiteConversionOptimizationIcon />,
      },
      {
        title: 'Brand Management Center',
        icon: <BrandManagementCenterIcon />,
      },
      {
        title: 'Enterprise-Grade Security, SSO & Governance',
        icon: <EnterpriseGradeSecurityIcon />,
      },
      {
        title: 'Admin & Role Management',
        icon: <AdminRoleManagementIcon />,
      },
      {
        title: 'Dedicated Customer Success Manager',
        icon: <DedicatedCSMIcon />,
      },
      {
        title: 'Full API Access',
        icon: <APIAccessIcon />,
      },
    ],
  },

  [SUBSCRIPTION_PLAN.enterprise]: {
    label: 'Enterprise',
    description:
      'Leverage your historical data and brand guidelines to create content that works for your specific audience.',
    price: 999,
    creditsPerMonth: 'Unlimited',
    seats: 'Custom',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Write from scratch or\nimprove your existing landing\npages continuously',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Dedicated customer\nsuccess manager',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: 'Everything in Data-Driven Unlimited, plus:',
        isHeader: true,
      },
      FEATURE_API_ACCESS,
      {
        text: 'Brand guidelines filter',
      },
      {
        text: 'Custom models',
      },
      {
        text: 'Enterprise-grade services & integrations',
      },
      {
        text: 'Custom number of seats',
      },
    ],
  },
  [SUBSCRIPTION_PLAN.starter]: {
    label: 'Starter',
    description: 'For solo marketers & freelancers who want to write professional copy 10x faster.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    seats: 1,
    price: 49,
    priceAnnually: 468,
    priceAnnuallyPerMonth: 39,
    percentOfAnnualDiscount: 20,
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      { text: '5 one-click Boost Performance' },
      { text: '100+ performance-driven templates' },
      { text: 'Blog wizard' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'High-performing talking point suggestions' },
      { text: '1 tone of voice' },
      { text: '2 target audiences' },
      { text: '1 messaging bank assets' },
      { text: '1 custom copywriting formulas' },
      { text: '3 brand rules' },
      { text: '1 Workspace' },
    ],
    featureGroups: [
      {
        title: 'Data-Driven Copy Generation',
        icon: <CreateOutlinedIcon />,
        features: [
          '100+ performance-driven templates',
          'Blog Wizard',
          'Predictive performance scores & analytics',
          'High-performing talking point suggestions',
          '5 one-click boost performance / month',
        ],
      },
      {
        title: 'Brand voice',
        icon: <MicNoneOutlinedIcon />,
        features: [
          '1 tone of voice',
          '2 target audiences',
          '1 messaging bank asset',
          '1 custom copywriting formula',
          '3 brand rules',
        ],
      },
    ],
  },
  [SUBSCRIPTION_PLAN.dd_teams]: {
    label: 'Data-Driven Teams',
    description: 'For small marketing teams who want performance-focused and on-brand Gen AI.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    creditsPerMonth: 'Unlimited',
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
    features: [
      { text: 'Unlimited one-click Boost Performance' },
      { text: '100+ performance-driven templates' },
      { text: 'Blog wizard' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'High-performing talking point suggestions' },
      { text: '3 tones of voice' },
      { text: '3 target audiences' },
      { text: '3 messaging bank assets' },
      { text: '3 custom copywriting formulas' },
      { text: '5 brand rules' },
      { text: '5 Workspaces' },
    ],
    featureGroups: [
      {
        title: 'Data-Driven Copy Generation',
        icon: <CreateOutlinedIcon />,
        features: [
          '100+ performance-driven templates',
          'Blog wizard with plagiarism checker',
          'Predictive performance scores & analytics',
          'High-performing talking point suggestions',
          'Unlimited one-click boost performance',
        ],
      },
      {
        title: 'Brand voice',
        icon: <MicNoneOutlinedIcon />,
        features: [
          '3 tones of voice',
          '3 target audiences',
          '3 messaging bank assets',
          '3 custom copywriting formulas',
          '5 brand rules',
        ],
      },
    ],
  },

  // Obsolete plans
  [SUBSCRIPTION_PLAN.dataDriven100]: {
    label: 'Data-Driven',
  },

  [SUBSCRIPTION_PLAN.tiered]: {
    label: 'Starter',
  },

  [SUBSCRIPTION_PLAN.acquisition]: {
    label: 'Acquire',
  },

  [SUBSCRIPTION_PLAN.conversion]: {
    label: 'Convert',
  },

  [SUBSCRIPTION_PLAN.engagement]: {
    label: 'Engage',
  },
};

export const PLAN_DETAILS_TABLE_ROWS = [
  {
    key: 'creditsPerMonth',
    label: 'Word credits per month',
  },
  {
    key: 'realtimeScoringAndAnalytics',
    label: 'Real-time scoring and analytics',
    subLabel: 'Get real-time scoring and analytics for every copy edit you make',
  },
  {
    key: 'scoreOptimization',
    label: 'Score optimization',
    subLabel: 'Have Anyword rewrite any existing copy to improve its predictive performance score',
  },
  {
    key: 'seats',
    label: 'Seats',
  },
  {
    key: 'blogPosts',
    label: 'Blog posts',
  },
  {
    key: 'aiWritingTools',
    label: 'AI writing tools',
  },
  {
    key: 'social',
    label: 'Social',
    subLabel: 'Channel-specific data-driven copywriting optimized for performance',
  },
  {
    key: 'ads',
    label: 'Ads',
    subLabel: 'Channel-specific data-driven copywriting optimized for performance',
  },
  {
    key: 'landingPages',
    label: 'Landing pages',
  },
  {
    key: 'subjectLines',
    label: 'Subject lines, cold emails,\nProspecting emails',
  },
  {
    key: 'productListings',
    label: 'Product Listings',
    subLabel: 'Amazon, Shopify, Walmart',
  },
  {
    key: 'customAiModels',
    label: 'Custom AI models',
  },
  {
    key: 'policyComplianceDetection',
    label: 'Ad policy compliance\ndetection',
  },
  {
    key: 'importAdsFromSocial',
    label: 'Import your ads from\nFacebook & Google',
  },
  {
    key: 'copyVariationsInformed',
    label: 'Copy variations informed\nby customer reviews',
  },
  {
    key: 'apiAccess',
    label: 'API access',
  },
  {
    key: 'support',
    label: 'Support',
  },
];

export const PLAN_DETAILS_TABLE = [
  {
    type: 'header',
    text: 'Plans Include',
  },
  {
    text: 'Words',
    data: ['Unlimited', 'Unlimited', 'Unlimited'],
  },
  {
    text: 'Seats',
    data: ['1', '3 included, \n up to 10 available', 'Custom'],
  },
  {
    text: 'Price per Additional Seat',
    data: ['-', '$59/mo or $49/mo yearly, \n up to 10 users', '-'],
  },
  {
    text: 'Workspaces',
    data: [1, 5, 'Custom'],
  },
  {
    type: 'header',
    text: 'Copy Generation Suite & Tools',
  },
  {
    text: '100+ Performance-Driven Templates',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Data-Driven Editor',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: '1-Click Boost Performance',
    data: ['5 / month', 'Unlimited', 'Unlimited'],
  },
  {
    text: 'Blog Wizard',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Plagiarism Checker',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Languages',
    data: ['30+', '30+', '30+'],
  },
  {
    text: 'Grammarly Integration',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    type: 'header',
    text: 'Copy Intelligence',
  },
  {
    text: 'Predictive Performance Scores & Analytics',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Custom Copy Scoring AI Models Based on Your Data',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Performance Data ',
    data: ['-', '-', 'Unlimited copy assets \n & connected channels'],
  },
  {
    text: 'Unlimited custom copy scoring models',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'High-Performance Talking Points',
    data: ['-', '-', 'Based on Anyword’s data\n on all connected channels'],
  },
  {
    text: 'Website Conversion Copy Campaigns ',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, 'Unlimited campaigns \n 100K visits/mo'],
  },
  {
    type: 'header',
    text: 'Brand Voice',
  },
  {
    text: 'Tone of Voice',
    data: ['1 tone', '3 tones', 'Unlimited'],
  },
  {
    text: 'Target Audiences',
    data: ['2 audiences', '3 audiences', 'Unlimited'],
  },
  {
    text: 'Messaging Bank',
    data: ['1 asset', '3 assets', 'Unlimited'],
  },
  {
    text: 'Custom Formulas',
    data: ['1 formula', '3 formulas', 'Unlimited'],
  },
  {
    text: 'Brand Rules',
    data: ['3 rules', '5 rules', 'Unlimited'],
  },
  {
    type: 'header',
    text: 'Admin, Support & Security',
  },
  {
    text: 'Customer Success',
    data: ['Knowledge base & email', 'Knowledge base & email', 'Dedicated Success Manager'],
  },
  {
    text: 'SSO',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Data Privacy',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Reliability with 99% Uptime',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'API',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Admin & Role Management',
    data: ['-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Custom Formulas',
    data: ['1 formula', '5 formulas', 'Unlimited'],
  },
];

export const getPlanProperty = (plan, property, defaultValue = undefined) => {
  const props = PLAN_PROPS[plan];
  return (props && props[property]) || defaultValue;
};

export const getPlanFeatureGroups = (plan) => getPlanProperty(plan, 'featureGroups', []);

export const getSubscriptionPlanLabel = (plan) => getPlanProperty(plan, 'label', '');

export const getSubscriptionPlanSubtitle = (plan) => getPlanProperty(plan, 'subtitle', '');

export const getSubscriptionPlanDescription = (plan) => getPlanProperty(plan, 'description', '');

export const getSubscriptionPlanWordsLimit = (plan) => getPlanProperty(plan, 'wordsLimit', 0);

export const getSubscriptionPlanFeatures = (plan) => getPlanProperty(plan, 'features', []);

export const getSubscriptionPlanTiers = (plan, defaultValue) =>
  getPlanProperty(plan, 'tiers', defaultValue);

export const getSubscriptionDetails = (plan, key) => getPlanProperty(plan, key, '');
